/**
 * <mb-cart-suggested>: "compre também" do carrinho (portado de common/groupShopCart.js).
 * Os produtos vêm prontos no HTML (coleção de custom.compra_sugerida) ou, como no site atual, da tag
 * `compra-sugerida-*` do último item: `data-tag-url` aponta para /collections/all/<tag>?section_id=vitrine-tag,
 * que devolve um slide e um modal por produto. O carrossel é montado depois, a partir do <template>.
 * O botão ADICIONAR abre o modal de variantes do produto (mesma mecânica do site atual) e a escolha
 * vai para /cart/add.js; depois o carrinho inteiro é re-renderizado pelo <mb-cart-drawer>.
 */
const { emit, withUsdPrice } = window.MB;

class MbCartSuggested extends HTMLElement {
  connectedCallback() {
    if (this.dataset.tagUrl) this.load();
    else this.build(this.querySelector('[data-suggested-items]'));

    this.addEventListener('click', (event) => {
      const open = event.target.closest('[data-suggested-open]');
      if (open) {
        event.preventDefault();
        this.openModal(open.dataset.suggestedOpen);
        return;
      }

      const close = event.target.closest('[data-suggested-close]');
      if (close) {
        event.preventDefault();
        this.closeModals();
        return;
      }

      const variant = event.target.closest('[data-suggested-variant]');
      if (variant) {
        event.preventDefault();
        this.add(variant);
      }
    });
  }

  async load() {
    try {
      const response = await fetch(this.dataset.tagUrl);
      if (!response.ok) throw new Error(String(response.status));
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const inCart = this.dataset.cartIds || '';
      const box = document.createElement('div');
      doc.querySelectorAll('[data-vitrine-tag="suggested"] [data-vitrine-item]').forEach((item) => {
        if (!inCart.includes(`,${item.dataset.vitrineItem},`)) box.append(...item.children);
      });
      this.build(box);
    } catch (error) {
      this.remove();
    }
  }

  /** Monta o carrossel com os slides de `source` e deixa os modais no fim do componente. */
  build(source) {
    const slides = source ? [...source.querySelectorAll('[data-suggested-slide]')] : [];
    const modals = source ? [...source.querySelectorAll('.modal-cart-product')] : [];
    const template = this.querySelector('template[data-suggested-carousel]');
    if (!slides.length || !template) {
      this.remove();
      return;
    }
    const carousel = template.content.cloneNode(true);
    carousel.querySelector('.swiper-wrapper').append(...slides);
    template.replaceWith(carousel);
    this.append(...modals);
    source.remove();
    this.hidden = false;
  }

  get root() {
    return this.closest('mb-cart-drawer, mb-cart-page');
  }

  openModal(id) {
    this.closeModals();
    const modal = this.querySelector(`[data-modal-id="${id}"]`);
    if (!modal) return;
    modal.classList.add('-active');
  }

  closeModals() {
    this.querySelectorAll('.modal-cart-product.-active').forEach((modal) => {
      modal.classList.remove('-active');
    });
  }

  async add(button) {
    const modal = button.closest('.modal-cart-product');
    const error = modal?.querySelector('[data-suggested-error]');
    if (error) error.textContent = '';
    button.classList.add('-adding');

    try {
      const root = this.root;
      const response = await fetch(`${window.theme.routes.cartAdd}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          ...withUsdPrice({ id: Number(button.dataset.suggestedVariant), quantity: 1 }, Number(button.dataset.suggestedPrice)),
          sections: root ? root.sectionId : undefined,
        }),
      });
      const data = await response.json();
      if (!response.ok) throw data;

      const html = data.sections && root && data.sections[root.sectionId];
      if (root) await root.refresh(typeof html === 'string' ? html : undefined);
      emit('mb:cart:updated', { item: data, fromDrawer: true });
    } catch (data) {
      const strings = (window.theme && window.theme.strings) || {};
      if (error) error.textContent = data.description || data.message || strings.addError || '';
    }
    button.classList.remove('-adding');
  }
}

if (!customElements.get('mb-cart-suggested')) customElements.define('mb-cart-suggested', MbCartSuggested);
